/*
 * CSV corpus loader (FR-DATA-01) + row validator (FR-DATA-02).
 *
 * Responsibilities (this module only):
 *   - Fetch a CSV from a known location at app startup.
 *   - Decode as UTF-8, strip an optional BOM, tolerate blank lines and
 *     '#' comment lines.
 *   - Parse RFC-4180-ish CSV (double-quote escaping, embedded newlines
 *     inside quoted fields) into raw string records keyed by header.
 *   - Validate each row against the documented schema (FR-DATA-02);
 *     skip invalid rows with a `console.warn` naming the 1-based data
 *     row number and the failure reason, and log a load summary that
 *     includes the count of skipped rows.
 *   - Surface a user-facing error when zero valid rows survive
 *     validation, so the boot path can render it directly.
 *   - Hold the parsed corpus in module-level memory for the rest of the
 *     session (no re-parsing on subsequent calls).
 *
 * Layering: parseCsv is the unvalidated layer (returns RawRow[] with
 * no schema checks) and stays permissive on purpose so its callers can
 * choose their own validation policy. validateRow is the FR-DATA-02
 * layer and runs inside loadCsv only — that keeps the parsing tests
 * decoupled from the schema and lets future scripts reuse parseCsv
 * with different rules if needed.
 *
 * Why hand-rolled (vs. PapaParse): PapaParse adds ~13 KB gzipped to a
 * tight 200 KB budget (NFR-05). Our CSV is internally produced and
 * well-formed, the parser fits in well under 100 LOC, and we keep zero
 * runtime dependencies. PapaParse becomes attractive only if the
 * content pipeline starts ingesting messy third-party CSVs.
 */

export type RuneEntryType = 'word' | 'short_line' | 'long_line';

export interface RuneEntry {
  latin: string;
  rune: string;
  english: string;
  level: number;
  type: RuneEntryType;
}

/**
 * A raw, *unvalidated* row keyed by CSV header name. Returned by
 * parseCsv so FR-DATA-02 can attach validation later without
 * restructuring the loader.
 */
export type RawRow = Record<string, string>;

const DEFAULT_CSV_URL = '/content/runes.csv';

let cachedCorpus: RuneEntry[] | null = null;
// Holds the in-flight load so concurrent loadCsv() calls share one fetch.
// Cleared on rejection so a retry actually retries.
let cachedPromise: Promise<RuneEntry[]> | null = null;

/**
 * Parse a CSV text blob into raw row objects.
 *
 * - Strips a UTF-8 BOM if present.
 * - Skips blank lines and lines whose first non-whitespace char is `#`
 *   (comment lines used in our fixtures).
 * - Honors RFC-4180 double-quote escaping: `""` inside a quoted field
 *   represents a literal `"`. Quoted fields may contain commas and
 *   newlines.
 * - Header row is the first non-comment, non-blank line.
 *
 * Returns an empty array if the input contains no data rows (loadCsv
 * decides what to do with that — FR-DATA-02 owns the empty-corpus
 * user-facing error).
 */
export function parseCsv(text: string): RawRow[] {
  const stripped = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const records = tokenizeCsv(stripped).filter((record) => {
    if (record.length === 0) return false;
    if (record.length === 1 && (record[0] ?? '') === '') return false;
    const first = (record[0] ?? '').trimStart();
    if (first.startsWith('#')) return false;
    return true;
  });

  if (records.length === 0) return [];
  const headerRecord = records[0];
  if (!headerRecord) return [];
  const headers = headerRecord.map((h) => h.trim());
  const rows: RawRow[] = [];

  for (let i = 1; i < records.length; i++) {
    const fields = records[i];
    if (!fields) continue;
    const row: RawRow = {};
    for (let j = 0; j < headers.length; j++) {
      const key = headers[j];
      if (!key) continue;
      row[key] = (fields[j] ?? '').trim();
    }
    rows.push(row);
  }

  return rows;
}

/**
 * Split a CSV text into records (arrays of field strings).
 * Handles quoted fields with embedded commas, newlines, and escaped
 * double quotes (`""`).
 */
function tokenizeCsv(text: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ',') {
      record.push(field);
      field = '';
      continue;
    }
    if (ch === '\r') {
      // CRLF normalization — peek and let the \n handler close the record.
      if (text[i + 1] === '\n') continue;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
      continue;
    }
    if (ch === '\n') {
      record.push(field);
      records.push(record);
      record = [];
      field = '';
      continue;
    }
    field += ch;
  }

  if (inQuotes) {
    // File ended in the middle of a quoted field. Fail loudly so loadCsv's
    // try/catch surfaces a "Failed to parse corpus" error rather than
    // letting a half-built record propagate.
    throw new Error('Unterminated quoted field at end of input');
  }

  // Trailing field/record (file may not end with a newline).
  if (field.length > 0 || record.length > 0) {
    record.push(field);
    records.push(record);
  }

  return records;
}

/**
 * User-facing error message shown when the corpus loads zero valid
 * rows. Kept as an exported constant so tests can assert the exact
 * wording without duplicating the string (FR-DATA-02 AC).
 */
export const CONTENT_LOAD_FAILED_MESSAGE =
  'Content failed to load — please contact the site administrator';

const REQUIRED_COLUMNS = ['latin', 'rune', 'english', 'level', 'type'] as const;
const VALID_TYPES: readonly RuneEntryType[] = ['word', 'short_line', 'long_line'];
const MIN_LEVEL = 1;
const MAX_LEVEL = 8;

/**
 * Strictly validate a RawRow against the documented FR-DATA-02 schema.
 * Returns a RuneEntry on success, or null when the row should be
 * skipped. On failure, emits a single `console.warn` naming the
 * 1-based row number (data rows only — the CSV header is not counted)
 * and the first failure reason encountered.
 *
 * Validation order matches the AC bullet order so warning text stays
 * predictable for users debugging their CSV. We stop at the first
 * failure per row because the audience for these warnings is a
 * content author fixing one issue at a time, not a CI system that
 * needs a complete report.
 */
function validateRow(row: RawRow, rowNumber: number): RuneEntry | null {
  // AC bullet 1: every row has the five required columns. parseCsv
  // populates missing columns as empty strings, so "missing column"
  // here means "the header row didn't include this key at all".
  for (const col of REQUIRED_COLUMNS) {
    if (!Object.prototype.hasOwnProperty.call(row, col)) {
      console.warn(`[corpus] row ${rowNumber} skipped: missing column "${col}"`);
      return null;
    }
  }

  const latin = row['latin'] ?? '';
  const rune = row['rune'] ?? '';
  const english = row['english'] ?? '';
  const levelRaw = row['level'] ?? '';
  const typeRaw = row['type'] ?? '';

  // AC bullet 4: latin, rune, english are non-empty strings. Checked
  // before level/type so a row with several errors fails on the
  // most-fundamental one first (a blank latin is a bigger smell than
  // a slightly-off level).
  if (latin === '') {
    console.warn(`[corpus] row ${rowNumber} skipped: empty "latin" field`);
    return null;
  }
  if (rune === '') {
    console.warn(`[corpus] row ${rowNumber} skipped: empty "rune" field`);
    return null;
  }
  if (english === '') {
    console.warn(`[corpus] row ${rowNumber} skipped: empty "english" field`);
    return null;
  }

  // AC bullet 2: level is an integer in [1, 8]. Number.parseInt
  // accepts trailing garbage ("1abc" -> 1), so guard with a regex to
  // require an integer-shaped token.
  if (!/^-?\d+$/.test(levelRaw)) {
    console.warn(
      `[corpus] row ${rowNumber} skipped: "level" is not an integer (got "${levelRaw}")`,
    );
    return null;
  }
  const level = Number.parseInt(levelRaw, 10);
  if (level < MIN_LEVEL || level > MAX_LEVEL) {
    console.warn(
      `[corpus] row ${rowNumber} skipped: "level" ${level} outside ${MIN_LEVEL}–${MAX_LEVEL}`,
    );
    return null;
  }

  // AC bullet 3: type is one of word | short_line | long_line. A blank
  // type usually means the row was short a column (parseCsv pads
  // short rows with empty strings rather than throwing) — call that
  // out specifically so content authors can tell "I forgot a comma"
  // apart from "I typed the wrong enum value".
  if (typeRaw === '') {
    console.warn(`[corpus] row ${rowNumber} skipped: missing "type" field`);
    return null;
  }
  if (!VALID_TYPES.includes(typeRaw as RuneEntryType)) {
    console.warn(
      `[corpus] row ${rowNumber} skipped: invalid "type" "${typeRaw}" (expected one of ${VALID_TYPES.join(', ')})`,
    );
    return null;
  }

  return {
    latin,
    rune,
    english,
    level,
    type: typeRaw as RuneEntryType,
  };
}

/**
 * Fetch and parse the CSV corpus. Caches the result for the rest of
 * the session — subsequent calls return the same array without
 * re-fetching or re-parsing (FR-DATA-01 AC: "no re-parsing during
 * normal use").
 *
 * On failure, throws an Error whose message names the file and the
 * failure reason so the caller can render it directly.
 */
export async function loadCsv(url: string = DEFAULT_CSV_URL): Promise<RuneEntry[]> {
  if (cachedCorpus !== null) return cachedCorpus;
  if (cachedPromise !== null) return cachedPromise;

  cachedPromise = fetchAndParse(url).catch((err: unknown) => {
    // Clear so the next call retries instead of inheriting the failure.
    cachedPromise = null;
    throw err;
  });
  return cachedPromise;
}

async function fetchAndParse(url: string): Promise<RuneEntry[]> {
  let response: Response;
  try {
    response = await fetch(url);
  } catch (cause) {
    throw new Error(`Failed to load corpus "${url}": ${describeError(cause)}`, {
      cause: cause instanceof Error ? cause : undefined,
    });
  }

  if (!response.ok) {
    throw new Error(
      `Failed to load corpus "${url}": HTTP ${response.status} ${response.statusText || ''}`.trim(),
    );
  }

  let text: string;
  try {
    text = await response.text();
  } catch (cause) {
    throw new Error(`Failed to read corpus "${url}": ${describeError(cause)}`, {
      cause: cause instanceof Error ? cause : undefined,
    });
  }

  let rows: RawRow[];
  try {
    rows = parseCsv(text);
  } catch (cause) {
    throw new Error(`Failed to parse corpus "${url}": ${describeError(cause)}`, {
      cause: cause instanceof Error ? cause : undefined,
    });
  }

  // FR-DATA-02: validate each row strictly, skip+warn on failure,
  // tally invalid rows so the summary log can report both counts.
  // Row numbering is 1-based and counts data rows only (the CSV
  // header isn't counted) so warning messages match how a content
  // author reads the source file.
  const corpus: RuneEntry[] = [];
  let invalidCount = 0;
  for (let i = 0; i < rows.length; i++) {
    const raw = rows[i];
    if (!raw) continue;
    const entry = validateRow(raw, i + 1);
    if (entry === null) {
      invalidCount++;
      continue;
    }
    corpus.push(entry);
  }

  // Emit the summary unconditionally — including on the zero-valid
  // failure path — so operators can see how many rows were attempted
  // and skipped alongside the user-facing error. cachedCorpus stays
  // gated behind the success path so an empty corpus is never cached.
  logCorpusSummary(corpus, invalidCount);

  if (corpus.length === 0) {
    // FR-DATA-02 AC: zero valid rows triggers the user-facing error.
    // We throw the canonical message directly (not wrapped with the
    // URL) so the boot path can render the verbatim AC wording. The
    // url is still observable via console.warn rows above.
    //
    // Throwing from inside fetchAndParse means loadCsv's catch clears
    // the cachedPromise, so a later retry (after content is fixed)
    // actually re-runs the load.
    throw new Error(CONTENT_LOAD_FAILED_MESSAGE);
  }

  cachedCorpus = corpus;
  return corpus;
}

/**
 * Synchronous accessor for code paths that run after `loadCsv` has
 * resolved (e.g. screen renderers). Throws if called before the
 * corpus has been loaded — that's a programmer error, not a runtime
 * condition we should swallow.
 */
export function getCorpus(): RuneEntry[] {
  if (cachedCorpus === null) {
    throw new Error('getCorpus() called before loadCsv() resolved');
  }
  return cachedCorpus;
}

/**
 * Test-only reset. Lets vitest start each case from a clean cache
 * without re-importing the module.
 */
export function __resetCorpusForTests(): void {
  cachedCorpus = null;
  cachedPromise = null;
}

function describeError(cause: unknown): string {
  if (cause instanceof Error) return cause.message || cause.name;
  if (typeof cause === 'string') return cause;
  return 'unknown error';
}

function logCorpusSummary(corpus: RuneEntry[], invalidCount: number): void {
  const byType = new Map<string, number>();
  const byLevel = new Map<number, number>();
  for (const entry of corpus) {
    byType.set(entry.type, (byType.get(entry.type) ?? 0) + 1);
    byLevel.set(entry.level, (byLevel.get(entry.level) ?? 0) + 1);
  }
  const typePart = [...byType.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
  const levelPart = [...byLevel.entries()]
    .sort(([a], [b]) => a - b)
    .map(([k, v]) => `L${k}=${v}`)
    .join(', ');
  // Single-line summary required by FR-DATA-01 AC, extended for
  // FR-DATA-02 to include the invalid-row count so the count of
  // skipped rows is observable in production logs.
  const skipped =
    invalidCount > 0
      ? ` (${invalidCount} invalid row${invalidCount === 1 ? '' : 's'} skipped)`
      : '';
  console.info(
    `[corpus] loaded ${corpus.length} entries${skipped} — types: ${typePart || '(none)'} — levels: ${levelPart || '(none)'}`,
  );
}
